import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Product } from '../types/Product';
import ProductShowcase from '../components/ProductShowcase';
import { useTranslation } from 'react-i18next';

const StyledHome = styled.div`
  min-height: 100vh;
`;

const MainContent = styled.div`
  padding: 2rem;
`;

const Hero = styled.div`
  height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-image: url('/images/hero-bg.jpg');
  background-size: cover;
  background-position: center;
  position: relative;
  margin-bottom: 2rem;
  padding: 1rem;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      var(--background-dark) 0%,
      transparent 50%,
      var(--background-dark) 100%
    );
    backdrop-filter: blur(5px);
  }

  @media (max-width: 768px) {
    height: 50vh;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  padding: 2rem;
  background: linear-gradient(
    135deg,
    rgba(0, 255, 213, 0.1) 0%,
    rgba(107, 75, 255, 0.1) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.3);

  h1 {
    font-size: 3rem;
    color: var(--primary-color);
    margin-bottom: 1rem;
    text-shadow: 
      0 0 10px var(--primary-glow),
      0 0 20px var(--primary-glow);
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
  
  p {
    font-size: 1.2rem;
    color: var(--text-primary);
    max-width: 600px;
    margin: 0 auto;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
    
    @media (max-width: 768px) {
      font-size: 1rem;
      padding: 0 1rem;
    }
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 0 1rem;
  }
`;

const ProductCard = styled.div`
  background: var(--surface-color);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 1.5rem;
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      45deg,
      transparent 0%,
      var(--primary-glow) 100%
    );
    opacity: 0;
    transition: opacity 0.3s ease;
    border-radius: 15px;
  }
  
  &:hover {
    transform: translateY(-5px);
    border-color: var(--primary-color);
    box-shadow: 
      0 5px 15px var(--primary-glow),
      0 0 30px var(--secondary-glow);
    
    &::before {
      opacity: 0.1;
    }
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 10px;
  margin-bottom: 1rem;
`;

const ProductTitle = styled.h2`
  color: var(--primary-color);
  margin-bottom: 0.5rem;
`;

const ProductSubtitle = styled.h3`
  color: var(--text-light);
  font-size: 0.9rem;
  margin-bottom: 1rem;
  opacity: 0.8;
`;

const ProductDescription = styled.p`
  color: var(--text-light);
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 1rem;
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const Tag = styled.span`
  background: var(--primary-glow);
  color: var(--primary-color);
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.8rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: var(--secondary-glow);
    color: var(--secondary-color);
    transform: translateY(-2px);
  }
`;

const Section = styled.section`
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  color: var(--primary-color);
  font-size: 2rem;
  margin-bottom: 2rem;
  text-align: center;
  text-shadow: 0 0 10px var(--primary-glow);
`;

const AboutContent = styled.div`
  color: var(--text-secondary);
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  background: var(--surface-color);
  padding: 2rem;
  border-radius: 15px;
  border: 1px solid var(--border-color);
  backdrop-filter: blur(10px);
`;

const ContactContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  color: var(--text-secondary);
  
  a {
    color: var(--primary-color);
    text-decoration: none;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    &:hover {
      text-shadow: 0 0 10px var(--primary-glow);
    }
  }
`;

export default function Home() {
  const [products, setProducts] = useState<Product[]>([]);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    fetch('/data/products.json')
      .then(res => res.json())
      .then(data => setProducts(data.products));
  }, []);

  return (
    <StyledHome>
      <Hero>
        <HeroContent>
          <h1>{t('company.name')}</h1>
          <p>{t('company.slogan')}</p>
        </HeroContent>
      </Hero>
      <ProductShowcase />
      <MainContent>
        <div id="products">
          <SectionTitle>{t('nav.products')}</SectionTitle>
          <ProductGrid>
            {products.map(product => (
              <ProductCard key={product.id}>
                <ProductImage 
                  src={product.imageUrl} 
                  alt={product.name}
                  loading="lazy"
                />
                <ProductTitle>
                  {i18n.language === 'en' ? product.englishName : product.name}
                </ProductTitle>
                <ProductSubtitle>{product.englishName}</ProductSubtitle>
                <ProductDescription>
                  {t(`products.${product.id}.description`, product.description)}
                </ProductDescription>
                <TagContainer>
                  {product.applications.map((app, index) => (
                    <Tag key={index}>
                      {t(`products.${product.id}.applications.${index + 1}`, app)}
                    </Tag>
                  ))}
                </TagContainer>
              </ProductCard>
            ))}
          </ProductGrid>
        </div>
      </MainContent>
      <Section id="about">
        <SectionTitle>{t('nav.about')}</SectionTitle>
        <AboutContent>
          <p>
            {t('about.description')}
          </p>
        </AboutContent>
      </Section>
      <Section id="contact">
        <SectionTitle>{t('nav.contact')}</SectionTitle>
        <ContactContent>
          <p>{t('contact.email.label')}</p>
          <a href="mailto:jlherun2008@126.com">
            <span>📧</span>
            jlherun2008@126.com
          </a>
        </ContactContent>
      </Section>
    </StyledHome>
  );
} 